import {createContext, use, useCallback, useId, useLayoutEffect, useMemo, useState, type PropsWithChildren} from 'react'
import {CounterLabel, Heading, IconButton} from '@primer/react'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import styles from './ForYouSubSection.module.css'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'

interface SectionVisibilityContextValue {
  register: () => () => void
}

const SectionVisibilityContext = createContext<SectionVisibilityContextValue | null>(null)

/**
 * A collapsible section wrapper styled to match the pull request inbox section
 * headers: a chevron toggle, the title, and a count of the visible items.
 *
 * The header and count are shown only when at least one child has called
 * `useRegisterAsVisible`; when no children are visible the whole section is
 * hidden, preventing orphaned headings and layout gaps.
 *
 * Children stay mounted while the section is collapsed (the body is hidden, not
 * unmounted) so their visibility registration keeps the count accurate and any
 * async work keeps running. Pass `defaultCollapsed` to start the section rolled
 * shut (e.g. "Completed").
 */
export function ForYouSubSection({
  title,
  defaultCollapsed = false,
  children,
}: PropsWithChildren<{title: string; defaultCollapsed?: boolean}>) {
  const [visibleCount, setVisibleCount] = useState(0)
  const [isExpanded, setIsExpanded] = useState(!defaultCollapsed)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const register = useCallback(() => {
    setVisibleCount(c => c + 1)
    return () => setVisibleCount(c => c - 1)
  }, [])

  const ctx = useMemo(() => ({register}), [register])

  const toggle = useCallback(() => {
    const next = !isExpanded
    setIsExpanded(next)
    sendPullRequestAnalyticsEvent('overview.section_toggle', 'OVERVIEW_FOR_YOU_SECTION', {
      section: title,
      is_open: next,
    })
  }, [isExpanded, sendPullRequestAnalyticsEvent, title])

  const bodyId = useId()

  return (
    <SectionVisibilityContext value={ctx}>
      <section className={visibleCount === 0 ? 'd-none' : undefined}>
        <div className={styles.header}>
          <IconButton
            className={styles.collapseButton}
            icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title}`}
            aria-expanded={isExpanded}
            aria-controls={bodyId}
            onClick={toggle}
            variant="invisible"
            size="small"
          />
          <Heading as="h3" variant="small" className={styles.heading}>
            {title}
          </Heading>
          <CounterLabel>{visibleCount}</CounterLabel>
        </div>
        {/* The body stays mounted while collapsed so children keep their
            visibility registration (and the count) accurate; `hidden` removes it
            from layout and the accessibility tree. */}
        <div id={bodyId} className={styles.body} hidden={!isExpanded}>
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <ul role="list" className={styles.list}>
            {children}
          </ul>
        </div>
      </section>
    </SectionVisibilityContext>
  )
}

/**
 * Call this hook from a component that renders visible content to register it
 * with the nearest `ForYouSubSection`. The section heading is shown whenever
 * at least one child is registered.
 *
 * Consumers of ForYouItem should early return null if they have no content to show
 * as once the ForYouItem is rendered we know it is visible and should be registered
 *
 * @example
 * useRegisterAsVisible()
 */
export function useRegisterAsVisible() {
  const ctx = use(SectionVisibilityContext)
  useLayoutEffect(() => {
    if (!ctx) return
    return ctx.register()
  }, [ctx])
}
